<template>
  <AuthPageLayout>
    <div class="auth-card">
      <div class="auth-card__header">
        <div class="auth-card__logo">
          <BrandLogo size="md" />
        </div>
        <div class="auth-card__icon">
          <i class="bi bi-phone"></i>
        </div>
        <h1 class="auth-card__title">ورود</h1>
        <p class="auth-card__subtitle">برای ادامه شماره موبایل خود را وارد کنید</p>
      </div>

      <ErrorMessage
        v-if="errorMessage"
        title="ورود انجام نشد"
        :message="errorMessage"
        :retryable="false"
        class="mb-3"
      />

      <form @submit.prevent="onSubmit" novalidate>
        <div class="mb-3">
          <label for="mobile" class="form-label">شماره موبایل</label>
          <div class="input-group">
            <span class="input-group-text"><i class="bi bi-telephone"></i></span>
            <input
              id="mobile"
              v-model.trim="mobile"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              class="form-control"
              :class="{ 'is-invalid': mobileError }"
              placeholder="مثال: 09123456789"
              @input="mobileError = ''"
            />
          </div>
          <div v-if="mobileError" class="invalid-feedback d-block">
            {{ mobileError }}
          </div>
        </div>

        <div class="mb-3">
          <label for="password" class="form-label">رمز عبور</label>
          <div class="input-group">
            <span class="input-group-text"><i class="bi bi-lock"></i></span>
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              class="form-control"
              :class="{ 'is-invalid': passwordError }"
              placeholder="رمز عبور شما"
              @input="passwordError = ''"
            />
            <button
              type="button"
              class="btn btn-outline-secondary toggle-pass"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'مخفی کردن رمز عبور' : 'نمایش رمز عبور'"
            >
              <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
          <div v-if="passwordError" class="invalid-feedback d-block">
            {{ passwordError }}
          </div>
        </div>

        <div class="text-start mb-3">
          <router-link to="/forgot-password" class="auth-link">
            رمز عبور را فراموش کرده‌اید؟
          </router-link>
        </div>

        <button type="submit" class="btn auth-btn w-100" :disabled="loading">
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm ms-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ loading ? "در حال ورود..." : "ورود" }}
        </button>

        <p class="text-center mt-3 mb-0 auth-switch">
          حساب کاربری ندارید؟
          <router-link to="/signup">ثبت‌نام</router-link>
        </p>
      </form>
    </div>
  </AuthPageLayout>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import AuthPageLayout from "@/components/auth/AuthPageLayout.vue";
import BrandLogo from "@/components/BrandLogo.vue";
import { followAuthRedirect } from "@/utils/authSession";

const router = useRouter();
const route = useRoute();
const { login, loading, error } = useAuth();

const mobile = ref("");
const password = ref("");
const showPassword = ref(false);
const mobileError = ref("");
const passwordError = ref("");
const errorMessage = ref("");

const MOBILE_PATTERN = /^\+?\d{7,15}$/;

function normalizeMobile(value) {
  return value.replace(/[\s-]/g, "");
}

function validate() {
  let ok = true;
  mobileError.value = "";
  passwordError.value = "";

  const normalized = normalizeMobile(mobile.value);
  if (!normalized) {
    mobileError.value = "لطفاً شماره موبایل خود را وارد کنید.";
    ok = false;
  } else if (!MOBILE_PATTERN.test(normalized)) {
    mobileError.value = "شماره موبایل معتبر وارد کنید (فقط عدد).";
    ok = false;
  }

  if (!password.value) {
    passwordError.value = "لطفاً رمز عبور خود را وارد کنید.";
    ok = false;
  }

  return ok;
}

async function onSubmit() {
  errorMessage.value = "";
  if (!validate()) return;

  const success = await login(normalizeMobile(mobile.value), password.value);

  if (success) {
    const redirect =
      typeof route.query.redirect === "string" ? route.query.redirect : "/dashboard";
    followAuthRedirect(redirect, "/dashboard", (path) => {
      router.push(path);
    });
  } else {
    errorMessage.value = error.value || "شماره موبایل یا رمز عبور نادرست است.";
  }
}
</script>
